import React, { useEffect, useState, useContext } from "react";
import { statusApi, handleApiError } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";

const StatusList = ({ onEdit, refreshTrigger }) => {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { user, isAuthenticated } = useContext(AuthContext);

  console.log("🔍 StatusList render - Auth state:", {
    user: user,
    isAuthenticated: isAuthenticated,
    hasUser: !!user,
    userRole: user?.role,
  });

  useEffect(() => {
    console.log("🚀 StatusList mounted, user:", !!user);
    if (user) {
      fetchStatuses();
    } else {
      console.log("⚠️ No user found, skipping fetch");
      setLoading(false);
    }
  }, [refreshTrigger, user]);

  const fetchStatuses = async () => {
    setLoading(true);
    setError(null);

    try {
      // HIPAA Compliant: Using statusApi which uses secureAxios with httpOnly cookies
      const response = await statusApi.getAll();
      setStatuses(response.data);
    } catch (err) {
      console.error("❌ Failed to load statuses", err);
      const apiError = handleApiError(err);

      // Don't show auth errors if user is not authenticated
      if (err.response?.status === 401 && !user) {
        setError("Please log in to view statuses.");
      } else {
        setError(apiError.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, statusName) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the status "${statusName}"?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      // HIPAA Compliant: Using statusApi with secure httpOnly cookie auth
      await statusApi.delete(id);

      // Remove from local state
      setStatuses((prev) => prev.filter((s) => s.status_id !== id));

      // Success notification
      alert("✅ Status deleted successfully!");
    } catch (err) {
      console.error("❌ Failed to delete status", err);

      const apiError = handleApiError(err);
      if (err.response?.status === 409) {
        alert("❌ " + apiError.message);
      } else {
        alert("❌ Failed to delete status: " + apiError.message);
      }
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="text-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
        {user && (
          <button
            className="btn btn-sm btn-outline-danger ms-3"
            onClick={fetchStatuses}
          >
            Retry
          </button>
        )}
      </div>
    );
  }

  // Show empty state
  if (statuses.length === 0) {
    return (
      <div className="alert alert-info" role="alert">
        No statuses found.
        {user?.role === "admin" &&
          " Create your first status using the 'Create Status' tab."}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4>Status Management</h4>
        <span className="badge bg-secondary">{statuses.length} statuses</span>
      </div>

      {/* Desktop/Tablet Table View */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th scope="col">Status</th>
              <th scope="col">Description</th>
              <th scope="col">Status Type</th>
              <th scope="col">Last Updated</th>
              <th scope="col" className="text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {statuses.map((s) => (
              <tr key={s.status_id}>
                <td>
                  <strong>{s.status}</strong>
                </td>
                <td>
                  {s.status_description || (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  {s.status_type_name ? (
                    <span className="badge bg-info text-dark">
                      {s.status_type_name}
                    </span>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  <small className="text-muted">
                    {new Date(
                      s.updated_at || s.created_at
                    ).toLocaleDateString()}
                  </small>
                </td>
                <td className="text-end">
                  {user?.role === "admin" ? (
                    <>
                      <button
                        className="btn btn-sm btn-outline-primary me-2"
                        onClick={() => onEdit(s)}
                        title="Edit status"
                        aria-label={`Edit status ${s.status}`}
                      >
                        <i className="bi bi-pencil" aria-hidden="true"></i> Edit
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDelete(s.status_id, s.status)}
                        title="Delete status"
                        aria-label={`Delete status ${s.status}`}
                      >
                        <i className="bi bi-trash" aria-hidden="true"></i>{" "}
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-muted">View only</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile-Friendly Card View */}
      <div className="d-block d-md-none">
        {statuses.map((s) => (
          <div key={s.status_id} className="card mb-3">
            <div className="card-body">
              <h5 className="card-title">{s.status}</h5>

              {s.status_type_name && (
                <span className="badge bg-info text-dark mb-2">
                  {s.status_type_name}
                </span>
              )}

              <p className="card-text">
                {s.status_description || (
                  <span className="text-muted">No description</span>
                )}
              </p>

              <p className="card-text">
                <small className="text-muted">
                  Last updated:{" "}
                  {new Date(s.updated_at || s.created_at).toLocaleDateString()}
                </small>
              </p>

              {user?.role === "admin" && (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => onEdit(s)}
                    aria-label={`Edit status ${s.status}`}
                  >
                    <i className="bi bi-pencil" aria-hidden="true"></i> Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(s.status_id, s.status)}
                    aria-label={`Delete status ${s.status}`}
                  >
                    <i className="bi bi-trash" aria-hidden="true"></i> Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default StatusList;
